import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@nusign/picflow-backend-types';
import { catchError, firstValueFrom, map, throwError, timer } from 'rxjs';
import { GROWTHBOOK } from '../growthbook';
import { loadScript } from '../lib/load-script';
import { WITH_AUTH } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { UploadService } from './upload.service';

export interface DropboxFile {
  name: string;
  url: string;
  size: number;
  source: 'dropbox';
}

@Injectable({
  providedIn: 'root',
})
export class DropboxService {
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private uploadService = inject(UploadService);
  private growthBook = inject(GROWTHBOOK);

  constructor() {
    this.uploadService.alternativeUploadServices.push({
      source: 'dropbox',
      service: this,
    });
  }

  public async uploadFile(
    file: DropboxFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto,
    version_name?: string
  ): Promise<AssetDto> {
    return firstValueFrom(
      this.http
        .post<AssetDto>(
          this.config.internalApiUrlBase + '/upload-from-url',
          {
            gallery: galleryId,
            section: sectionId,
            asset_name: file.name,
            position,
            url: file.url,
            asset_to_replace: assetToReplace?.id,
            version_name,
          },
          { context: new HttpContext().set(WITH_AUTH, true) }
        )
        .pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              if (err.status === 503) {
                return timer(10_000).pipe(map(() => undefined));
              }
            }
            return throwError(() => err);
          })
        )
    );
  }

  public async startChooser(): Promise<DropboxFile[]> {
    await loadScript('https://www.dropbox.com/static/api/2/dropins.js', {
      id: 'dropboxjs',
      attributes: {
        'data-app-key': 'd5tpqqbp7tfoqs8',
      },
    });

    return new Promise(resolve => {
      const options = {
        success: files => {
          resolve(
            files.map(file => {
              return <DropboxFile>{
                name: file.name,
                url: file.link,
                size: file.bytes,
                source: 'dropbox',
              };
            })
          );
        },
        cancel: function () {
          resolve([]);
        },
        linkType: 'direct',
        multiselect: true,
        folderselect: false, // or true
      };
      new window['Dropbox'].choose(options);
    });
  }
}
